<template>
  <UiPopup
    :class="$style.root"
    data-testid="privacy-policy"
    size="lg"
    :classes="{ content: 'PrivacyPolicy__content' }"
    @close="onClose"
  >
    <div v-html="contentHtml"/>
  </UiPopup>
</template>


<script>
import { marked } from 'marked';
import { store } from '@/app/store';
import PRIVACY_AUSTRALIA from '@/privacy/australia_policy.md?raw';
import PRIVACY_BR from '@/privacy/br_policy.md?raw';
import PRIVACY_CA from '@/privacy/ca_policy.md?raw';
import PRIVACY_CH from '@/privacy/ch_policy.md?raw';
import PRIVACY_EU from '@/privacy/eu_policy.md?raw';
import PRIVACY_MX from '@/privacy/mx_policy.md?raw';
import PRIVACY_PE from '@/privacy/pe_policy.md?raw';
import PRIVACY_SINGAPORE from '@/privacy/singapore_policy.md?raw';
import PRIVACY_UK from '@/privacy/uk_policy.md?raw';
import PRIVACY_US from '@/privacy/us_policy.md?raw';
import PRIVACY_US_POPUP from '@/privacy/us_policy_popup.md?raw';
import { sentryService } from '@/services/telemetry/sentry';
import { UiPopup } from '@/ui-kit';

const PRIVACY_DICT = {
  eu_policy: PRIVACY_EU,
  us_policy: PRIVACY_US,
  uk_policy: PRIVACY_UK,
  australia_policy: PRIVACY_AUSTRALIA,
  singapore_policy: PRIVACY_SINGAPORE,
  br_policy: PRIVACY_BR,
  ca_policy: PRIVACY_CA,
  ch_policy: PRIVACY_CH,
  mx_policy: PRIVACY_MX,
  pe_policy: PRIVACY_PE,
  us_policy_popup: PRIVACY_US_POPUP,
};

/**
 * Strip <script> tags and on* event attributes from HTML to prevent XSS.
 * The markdown sources are bundled at build time from .md files and are
 * trusted, but defense-in-depth protects against accidental injection
 * if a markdown file contains raw HTML.
 */
function sanitizeHtml(html) {
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/\bon\w+\s*=\s*(?:"[^"]*"|'[^']*'|`[^`]*`|[^\s>]+)/gi, '');
}

export default {
  components: { UiPopup },
  emits: ['close'],
  data() {
    return {
      contentHtml: null,
    };
  },
  mounted() {
    sentryService.addBreadcrumb('ui.display', 'Privacy policy displayed', {
      locale: store.currentLocale?.code,
      region: store.kiosk?.region || 'unknown',
    });

    // Prefer the editable per-kiosk policy profile (THE-298) when present;
    // a locale missing from the profile falls back to the profile's English
    // text before the bundled per-variant markdown, so a partially-translated
    // profile never silently swaps to entirely different legacy text.
    const locale = store.currentLocale?.code;
    const policyContent = store.kiosk?.policy_content;
    const profilePrivacyText =
      policyContent?.[locale]?.privacy_text || policyContent?.en?.privacy_text;
    const privacyPolicyMarkdown =
      profilePrivacyText ||
      PRIVACY_DICT[store.kiosk?.privacy_policy_variant] ||
      PRIVACY_DICT.eu_policy;
    this.contentHtml = sanitizeHtml(marked(privacyPolicyMarkdown));
  },
  methods: {
    /**
     * Forward close event from inner Popup to parent.
     * Without this, the Popup's close button, backdrop click, and Escape key
     * all emit 'close' but PrivacyPolicy swallowed it -- the user was trapped.
     */
    onClose() {
      this.$emit('close');
    },
  },
};
</script>


<style module>
.root {
  --ui-popup-content-width: var(--kiosk-privacy-policy-content-width);
  --ui-popup-content-max-width: var(--kiosk-privacy-policy-content-max-width);

  text-align: left;
  font-family: var(--kiosk-font-family-body);
  font-size: var(--kiosk-privacy-policy-font-size);
  line-height: 1.4;
}

.root :global(h1) {
  font-size: 1.8rem;
  margin-top: 1.6rem;
}

.root :global(h2) {
  font-size: 1.5rem;
  margin-top: 1.4rem;
}

.root :global(h3) {
  font-size: 1.5rem;
  margin-top: 1.2rem;
}

.root :global(p),
.root :global(h1),
.root :global(h2),
.root :global(h3) {
  margin-bottom: 1rem;
}

.root :global(p:first-child),
.root :global(h1:first-child),
.root :global(h2:first-child),
.root :global(h3:first-child) {
  margin-top: 0;
}

.root :global(p:last-child),
.root :global(h1:last-child),
.root :global(h2:last-child),
.root :global(h3:last-child) {
  margin-bottom: 0;
}

</style>
